// source-link.ts — pure logic for RAG source-citation clicks.
//
// On surface=="portal" the widget intercepts a citation click and dispatches a
// composed `emma-chat:open-source` DOM event that bubbles to the <emma-chat>
// host, where the portal (EmmaChatLauncher.vue) opens the in-app Docsie sidebar
// (via $bus 'callDocsieArticle') when an art_* id is present, else falls back to
// window.open(url). On any other surface the citation keeps its native
// <a target="_blank"> new-tab behaviour. Kept pure (no Lit) so the detail
// contract + extraction are unit-testable, matching carry-deploy-events.test.ts.

import type { SourceCitation, OpenSourceDetail } from '../types/sse.js';

/** Wire name of the cross-shadow-DOM event the portal host listens for. */
export const OPEN_SOURCE_EVENT = 'emma-chat:open-source';

/** Portal is the only surface that intercepts citation clicks (in-app Docsie
 *  sidebar); hero / standalone embeds keep the native new-tab link. */
export function shouldInterceptSourceClick(surface: string): boolean {
  return surface === 'portal';
}

/** Pull the Docsie `art_*` id from anywhere in a url (path or query), if one is
 *  present. The current BFF citation url is docs.emma.ms/?doc=<slugPath> and
 *  carries NO art_*, so this usually returns undefined — kept so a future url
 *  shape (or a source that embeds the id) still resolves the in-app sidebar. */
function extractArticleId(url: string): string | undefined {
  const m = url.match(/art_[A-Za-z0-9]+/);
  return m ? m[0] : undefined;
}

/** Pull the slug / doc-path from the Docsie public url docs.emma.ms/?doc=<path>.
 *  Parses only the query string so a relative or malformed url can't throw. */
function extractDocSlug(url: string): string | undefined {
  const q = url.indexOf('?');
  if (q < 0) return undefined;
  const doc = new URLSearchParams(url.slice(q + 1)).get('doc');
  return doc ? doc : undefined;
}

/** Build the `emma-chat:open-source` detail from a citation. `url` is required
 *  (only citations WITH a url are clickable). articleId / slug are best-effort:
 *  prefer fields the source carries, else derive from the url. Omitted keys stay
 *  absent (not empty strings) so the portal's `articleId ? sidebar : window.open`
 *  branch reads cleanly. */
export function buildOpenSourceDetail(s: SourceCitation): OpenSourceDetail {
  const url = s.url ?? '';
  const articleId = s.articleId || extractArticleId(url);
  const slug = s.slug || extractDocSlug(url);
  const detail: OpenSourceDetail = { url };
  if (articleId) detail.articleId = articleId;
  if (slug) detail.slug = slug;
  if (s.title) detail.title = s.title;
  return detail;
}
